import { createComment } from "@/server/comments";
import { useState } from "react";
import { PillToggle } from "../../ui/pillToggle";
import { Button } from "@/components/ui/button";
import { Comment } from "@/apiTypes/comment";

const NON_ORGANIZER_CHAR_LIMIT = 1000;

/**
 * Textarea-based comment input with character counting for non-admins.
 * Organizer admins see a Comment/Update pill toggle and can post either type.
 * Calls `createComment` on submit and notifies the parent via `onSubmit`.
 */
export function CommentComposer({
  eventId,
  isOrganizerAdmin,
  onSubmit,
}: {
  eventId: string;
  isOrganizerAdmin: boolean;
  onSubmit: (comment: Comment) => void;
}) {
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [composerMode, setComposerMode] = useState<"Comment" | "Update">(
    "Comment",
  );

  const postingAsUpdate = composerMode === "Update";
  const overLimit =
    !isOrganizerAdmin &&
    !postingAsUpdate &&
    content.length > NON_ORGANIZER_CHAR_LIMIT;
  const isDisabled = isSubmitting || content.trim().length === 0 || overLimit;

  async function handleSubmit() {
    if (isDisabled) return;
    setIsSubmitting(true);
    try {
      const type = postingAsUpdate ? "UPDATE" : "COMMENT";
      const newComment = await createComment({
        data: { eventId, content: content.trim(), type },
      });
      onSubmit(newComment);
      setContent("");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="space-y-2"
    >
      {isOrganizerAdmin && (
        <PillToggle
          options={["Comment", "Update"]}
          value={composerMode}
          onChange={(v) =>
            setComposerMode(v === "Update" ? "Update" : "Comment")
          }
          className="bg-gray-100 border border-gray-200"
          highlightColor="bg-secondary-400"
        />
      )}
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={postingAsUpdate ? "Write an update…" : "Write a comment…"}
        aria-label={postingAsUpdate ? "Update" : "Comment"}
        rows={3}
        className="w-full text-sm rounded-md border border-gray-200 px-3 py-2 resize-none bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      />
      <div className="flex items-center justify-between">
        {!isOrganizerAdmin && !postingAsUpdate ? (
          <span
            className={`text-xs tracking-wide ${overLimit ? "text-red-600 font-medium" : "text-gray-400"}`}
          >
            {content.length}/{NON_ORGANIZER_CHAR_LIMIT}
          </span>
        ) : (
          <span />
        )}
        <Button type="submit" disabled={isDisabled} size="sm">
          {postingAsUpdate ? "Post Update" : "Post"}
        </Button>
      </div>
    </form>
  );
}
